import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Plus, Mail } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { PageHeader } from "@/components/site/PageParts";
import { SITE } from "@/siteData";
import { useMeta } from "@/useMeta";

const faqs = [
  {
    q: "Do my photos leave my phone?",
    a: "No. Selected photographs are processed locally and are not uploaded to Hildreth Technologies LLC.",
  },
  {
    q: "Do I need an account?",
    a: "No. The application does not require registration, login, or a customer account.",
  },
  {
    q: "Why does Android need internet access the first time?",
    a: "On some Android devices, Google Play services may need to download the on-device background-removal model. After it is installed, photograph processing occurs locally.",
  },
  {
    q: "Where are completed photos saved?",
    a: "Completed photographs are saved to the device’s Photos application or gallery when the user chooses Save.",
  },
  {
    q: "Are my original photos changed?",
    a: "No. Simple Background Remover creates a new completed photograph and does not overwrite the original.",
  },
  {
    q: "Why did part of my item disappear?",
    a: "Automatic background removal may occasionally have difficulty with thin details, lace, fringe, straps, reflective surfaces, or objects that closely match the original background. Review completed images before publishing them.",
  },
  {
    q: "How do I remove local app data?",
    link: true,
  },
];

const Item = ({ item, index, open, onToggle }) => (
  <div className="border-b border-line" data-testid={`faq-item-${index}`}>
    <button
      onClick={onToggle}
      className="flex w-full items-start justify-between gap-6 py-6 text-left"
      aria-expanded={open}
      data-testid={`faq-trigger-${index}`}
    >
      <span className="flex items-start gap-5">
        <span className="font-mono text-sm text-brand">{String(index + 1).padStart(2, "0")}</span>
        <span className="font-display text-lg font-bold tracking-tight text-ink sm:text-xl">
          {item.q}
        </span>
      </span>
      <motion.span
        animate={{ rotate: open ? 45 : 0 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className="mt-1 shrink-0 text-ink-faint"
      >
        <Plus size={20} />
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden"
        >
          <div className="pb-7 pl-[2.6rem] pr-8 text-base leading-relaxed text-ink-soft">
            {item.link ? (
              <span>
                Visit the{" "}
                <Link to="/data-deletion" className="link-underline font-semibold text-brand" data-testid="faq-data-deletion-link">
                  Data Deletion
                </Link>{" "}
                page.
              </span>
            ) : (
              item.a
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default function Support() {
  const [open, setOpen] = useState(0);

  useMeta({
    title: "Support | Simple Background Remover",
    description:
      "Support and frequently asked questions for Simple Background Remover. Contact support@hildrethtechnology.com for help.",
  });

  return (
    <>
      <PageHeader
        index="/support"
        eyebrow="Help center"
        title="Support"
        intro="Answers to common questions. Still stuck? Email us anytime."
      />
      <div className="container-x max-w-3xl py-16 sm:py-20">
        <Reveal>
          <div className="border-t border-line">
            {faqs.map((item, i) => (
              <Item
                key={i}
                item={item}
                index={i}
                open={open === i}
                onToggle={() => setOpen(open === i ? -1 : i)}
              />
            ))}
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-14 flex flex-col items-start gap-5 rounded-3xl border border-brand/20 bg-brand-tint/50 p-8 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-display text-xl font-bold tracking-tight text-ink">Contact Support</h2>
              <p className="mt-2 text-sm text-ink-soft">We read every message.</p>
            </div>
            <a
              href={`mailto:${SITE.email}`}
              className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-brand/25 transition-transform duration-300 hover:-translate-y-0.5"
              data-testid="support-email-button"
            >
              <Mail size={16} />
              {SITE.email}
            </a>
          </div>
        </Reveal>
      </div>
    </>
  );
}
